// Geräteübergreifender Abgleich von Übergabeprotokollen (Ticket #488, Björn).
//
// Bisher lag der Inhalt eines Protokolls bis zum Abschluss NUR auf dem Gerät, auf dem es
// bearbeitet wurde (IndexedDB, s. lokal.ts) — „im Büro am PC vorbereiten, vor Ort am Handy
// fertigstellen" ging nicht, das andere Gerät sah nur „auf einem anderen Gerät".
//
// Entscheidung Patrick 16.10.2026: AUTOMATISCHER Abgleich.
//  - Jede Änderung wird bei Netz im Hintergrund auf den Server geschoben, Fotos und
//    Unterschriften sofort hochgeladen (unter den Pfaden, die der Abschluss ohnehin nutzt).
//  - Beim Öffnen holt ein Gerät den neuesten Stand.
//  - Haben BEIDE Seiten seit dem letzten gemeinsamen Stand geändert, fragt die App.
//
// Versionsstand ist `updated_at` der Protokollzeile (setzt ein Trigger bei jedem Update).
// Ein Gerät merkt sich im Entwurf, auf welchem Stand es zuletzt war (`server_stand`), und
// schreibt nur, wenn der Server noch auf genau diesem Stand ist — sonst Konflikt.
//
// Der Abschluss bleibt wie er ist (abschluss.ts): Er schreibt den finalen Stand ohnehin
// komplett. Der Abgleich gilt nur, solange der Server-Status 'in_erfassung' ist; ein
// abgeschlossenes oder abgebrochenes Protokoll wird nie wiederbelebt.

import { supabase } from '../supabase/client';
import { medienPfade } from './kopieren';
import { ohneServerFelder } from './abschluss';
import type { LokalerEntwurf } from './lokal';
import type { Bezug } from './bezug';
import type { Protokoll } from './typen';

/** Was vom Server für die Entscheidung gebraucht wird. */
export interface ServerStand {
  status: string;
  updated_at: string;
  /** Trägt die Zeile schon Inhalt — oder ist sie nur die Kopfzeile aus der Vorauswahl? */
  hatInhalt: boolean;
  /**
   * Bezug aus der Kopfzeile. Öffnet ein zweites Gerät ein Protokoll, das bisher nur aus der
   * Kopfzeile besteht, muss der neue Entwurf DIESEN Bezug tragen — sonst entstünde ein
   * Entwurf ohne Objekt und Wohnung, und der Abgleich schriebe die Leere auf den Server.
   */
  bezug?: Bezug;
}

export type OeffnenEntscheidung =
  /** Lokal weiterarbeiten. `stand` = Server-Stand, den das Gerät ab jetzt als Basis führt. */
  | { art: 'lokal'; stand: string | null; hochschieben: boolean }
  /** Der Server ist weiter, lokal gibt es nichts Ungesichertes: Server-Stand übernehmen. */
  | { art: 'server' }
  /** Beide Seiten haben geändert — der Mensch entscheidet. */
  | { art: 'konflikt' }
  /** Kein lokaler Entwurf, Server hat Inhalt: vom Server holen. */
  | { art: 'vom_server' }
  /** Kein lokaler Entwurf, Server nur Kopfzeile (oder kein Server): neu anlegen wie bisher. */
  | { art: 'neu' };

/** Inhalt = irgendetwas, das über die Kopfzeile hinausgeht. */
export function zeileHatInhalt(z: Partial<Pick<Protokoll, 'raeume' | 'parteien' | 'zaehler' | 'schluessel' | 'bemerkungen' | 'unterschriften'>>): boolean {
  return (z.raeume?.length ?? 0) > 0
    || (z.parteien?.length ?? 0) > 0
    || (z.zaehler?.length ?? 0) > 0
    || (z.schluessel?.length ?? 0) > 0
    || (z.unterschriften?.length ?? 0) > 0
    || !!z.bemerkungen?.trim();
}

/**
 * Was tun beim Öffnen?
 *  - lokal: Entwurf auf dem Gerät mit `server_stand` und `ungesichert`
 *  - server: aktuelle Zeile; null = offline oder (noch) keine Zeile
 */
export function entscheideBeimOeffnen(
  lokal: Pick<LokalerEntwurf, 'server_stand' | 'ungesichert'> | null,
  server: ServerStand | null,
): OeffnenEntscheidung {
  // Offline, keine Zeile, oder schon fertig/abgebrochen: der Abgleich hält sich raus —
  // dafür gibt es die bestehenden Wege (Abschluss, „Lokalen Entwurf verwerfen").
  if (!server || server.status !== 'in_erfassung') {
    return lokal ? { art: 'lokal', stand: lokal.server_stand ?? null, hochschieben: false } : { art: 'neu' };
  }
  if (!lokal) return server.hatInhalt ? { art: 'vom_server' } : { art: 'neu' };

  const bekannt = lokal.server_stand ?? null;
  // Entwurf von vor diesem Abgleich (kein bekannter Stand): Ist der Server noch die leere
  // Kopfzeile, ist der lokale Entwurf die einzige Wahrheit. Hat inzwischen jemand anderes
  // Inhalt hochgeschoben, gibt es zwei Fassungen.
  if (bekannt === null) {
    return server.hatInhalt ? { art: 'konflikt' } : { art: 'lokal', stand: server.updated_at, hochschieben: true };
  }
  if (bekannt === server.updated_at) {
    return { art: 'lokal', stand: bekannt, hochschieben: !!lokal.ungesichert };
  }
  // Server ist weiter als das, was dieses Gerät zuletzt kannte.
  return lokal.ungesichert ? { art: 'konflikt' } : { art: 'server' };
}

/** Alle Mediendateien eines Protokolls: Fotos und Unterschriften. */
export function alleMedienPfade(p: Protokoll): string[] {
  const unterschriften = (p.unterschriften || []).map((u) => u.png_path).filter((x): x is string => !!x);
  return [...new Set([...medienPfade(p), ...unterschriften])];
}

/** Dateien, die lokal als Blob liegen, aber noch nicht hochgeladen sind. */
export function offeneUploads(e: Pick<LokalerEntwurf, 'fotos' | 'unterschriften' | 'hochgeladen'>): string[] {
  const schon = new Set(e.hochgeladen ?? []);
  return [...Object.keys(e.fotos || {}), ...Object.keys(e.unterschriften || {})].filter((p) => !schon.has(p));
}

/**
 * Was beim Hochschieben in die Zeile geschrieben wird: alles, was der Abschluss auch
 * schreibt, OHNE die serverseitigen Felder (abschluss.ts) und ohne Id, Status und
 * updated_at — den Status setzt nur der Abschluss, updated_at der Trigger.
 */
export function inhaltFuerServer(p: Protokoll): Record<string, unknown> {
  const { id: _id, status: _status, updated_at: _u, ...rest } = ohneServerFelder(p) as Protokoll;
  return rest;
}

// ── Ein-/Ausgabe, injizierbar für Tests (Muster AbschlussDeps) ──────────────────────

export interface SyncDeps {
  ladeServer(id: string): Promise<(Protokoll & { status: string; updated_at: string }) | null>;
  ladeHoch(pfad: string, blob: Blob): Promise<void>;
  /** Schreibt nur, wenn Status 'in_erfassung' und updated_at = erwartet. Neuer Stand oder null (Konflikt). */
  schreibe(id: string, inhalt: Record<string, unknown>, erwartet: string): Promise<string | null>;
  ladeMedium(pfad: string): Promise<Blob | null>;
}

export type HochschiebenErgebnis =
  | { art: 'gesichert'; stand: string; hochgeladen: string[] }
  | { art: 'konflikt'; hochgeladen: string[] }
  | { art: 'kein_stand' };

/** Neue Dateien hochladen, dann den Inhalt schreiben — nur, wenn der Server noch auf `server_stand` ist. */
export async function schiebeHoch(e: LokalerEntwurf, deps: SyncDeps): Promise<HochschiebenErgebnis> {
  if (!e.server_stand) return { art: 'kein_stand' };
  const hochgeladen: string[] = [];
  for (const pfad of offeneUploads(e)) {
    const blob = e.fotos[pfad] ?? e.unterschriften[pfad];
    if (!blob) continue;
    await deps.ladeHoch(pfad, blob);
    hochgeladen.push(pfad);
  }
  const neu = await deps.schreibe(e.id, inhaltFuerServer(e.protokoll), e.server_stand);
  return neu ? { art: 'gesichert', stand: neu, hochgeladen } : { art: 'konflikt', hochgeladen };
}

/**
 * Den Server-Stand als lokalen Entwurf aufbauen: Zeile übernehmen, Fotos und Unterschriften
 * herunterladen. Fehlende Dateien werden gezählt, nicht verschwiegen.
 * `basis` = vorhandener lokaler Entwurf; dessen Blobs werden wiederverwendet, statt sie
 * erneut zu laden.
 */
export async function holeVomServer(
  id: string, deps: SyncDeps, basis?: LokalerEntwurf | null,
): Promise<{ entwurf: LokalerEntwurf; fehlend: number } | null> {
  const zeile = await deps.ladeServer(id);
  if (!zeile) return null;
  const fotos: Record<string, Blob> = {};
  const unterschriften: Record<string, Blob> = {};
  const unterschriftPfade = new Set((zeile.unterschriften || []).map((u) => u.png_path));
  let fehlend = 0;
  for (const pfad of alleMedienPfade(zeile)) {
    const vorhanden = basis?.fotos[pfad] ?? basis?.unterschriften[pfad] ?? null;
    const blob = vorhanden ?? await deps.ladeMedium(pfad);
    if (!blob) { fehlend += 1; continue; }
    if (unterschriftPfade.has(pfad)) unterschriften[pfad] = blob; else fotos[pfad] = blob;
  }
  return {
    entwurf: {
      id,
      protokoll: zeile,
      fotos,
      unterschriften,
      // Namen/Orte vor dem Unterschreiben sind gerätelokal (keine Spalte) — die eines
      // vorhandenen Entwurfs bleiben erhalten.
      unterschrift_entwuerfe: basis?.unterschrift_entwuerfe ?? {},
      gespeichert_am: new Date().toISOString(),
      server_stand: zeile.updated_at,
      ungesichert: false,
      hochgeladen: alleMedienPfade(zeile),
    },
    fehlend,
  };
}

// ── Echte Anbindung ─────────────────────────────────────────────────────────────


const BUCKET = 'uebergaben';

/** Stand der Server-Zeile für die Entscheidung beim Öffnen; null = keine Zeile. */
export async function ladeServerStand(id: string): Promise<ServerStand | null> {
  const { data, error } = await supabase.from('uebergabeprotokolle')
    .select('status, updated_at, raeume, parteien, zaehler, schluessel, bemerkungen, unterschriften, art, objekt_id, einheit_id, mieter_id, eigentuemer_id')
    .eq('id', id).maybeSingle();
  if (error) throw error;
  if (!data) return null;
  return {
    status: data.status as string,
    updated_at: data.updated_at as string,
    hatInhalt: zeileHatInhalt(data as Partial<Protokoll>),
    bezug: {
      art: data.art, objekt_id: data.objekt_id, einheit_id: data.einheit_id,
      mieter_id: data.mieter_id, eigentuemer_id: data.eigentuemer_id, kaeufer_eigentuemer_id: null,
    } as Bezug,
  };
}

export function echteSyncDeps(): SyncDeps {
  return {
    async ladeServer(id) {
      const { data, error } = await supabase.from('uebergabeprotokolle').select('*').eq('id', id).maybeSingle();
      if (error) throw error;
      return (data as unknown as (Protokoll & { status: string; updated_at: string }) | null);
    },
    async ladeHoch(pfad, blob) {
      const { error } = await supabase.storage.from(BUCKET)
        .upload(pfad, blob, { upsert: true, contentType: blob.type || 'application/octet-stream' });
      if (error) throw error;
    },
    async schreibe(id, inhalt, erwartet) {
      // Optimistische Sperre: nur schreiben, wenn der Server noch auf dem Stand ist, den
      // dieses Gerät kennt, und das Protokoll noch läuft. 0 Zeilen = jemand war schneller.
      const { data, error } = await supabase.from('uebergabeprotokolle').update(inhalt)
        .eq('id', id).eq('status', 'in_erfassung').eq('updated_at', erwartet)
        .select('updated_at');
      if (error) throw error;
      return (data?.[0]?.updated_at as string | undefined) ?? null;
    },
    async ladeMedium(pfad) {
      const { data } = await supabase.storage.from(BUCKET).download(pfad);
      return data ?? null;
    },
  };
}
